// Echo-runde (Forslag B) — flash-and-recall.
//
// Et ord vises kort (kortere for korte ord), forsvinder så.
// Brugeren skriver det fra hukommelse og ser egne korrekte bogstaver.
// Forkert tast får ordet tilbage i halv tid som mild scaffold.
//
// Mellemrum mellem ord som diktat/sprint: når et ord er skrevet,
// vises en tydelig “tryk mellemrum”-cue før næste flash.

import { useEffect, useMemo, useRef, useState } from 'react';
import type {
  KeyboardLayoutId,
  RoundPresentation,
  RoundResult,
  Trin,
} from '../../types';
import type { PreparedRound } from '../../lib/lessonGenerator';
import { flashDurationForWord } from '../../lib/presentationTempo';
import { useRoundInput } from '../../lib/useRoundInput';
import { activeWordIndex, splitWords } from '../../lib/wordSpans';
import Keyboard from '../Keyboard';
import SpaceAdvanceHint from '../SpaceAdvanceHint';

interface EchoRoundProps {
  round: PreparedRound;
  trin: Trin;
  keyboardLayout: KeyboardLayoutId;
  strictCase?: boolean;
  allowAltLayer?: boolean;
  keyboardLayoutId?: KeyboardLayoutId;
  onFinish: (r: RoundResult) => void;
}

export default function EchoRound({
  round,
  trin,
  keyboardLayout,
  strictCase = false,
  allowAltLayer = false,
  keyboardLayoutId,
  onFinish,
}: EchoRoundProps) {
  const targetText = round.targetText;
  const mode = round.template.mode;
  const presentation = round.template.presentation as Extract<
    RoundPresentation,
    { kind: 'echo' }
  >;
  const baseFlashMs = Math.max(400, presentation?.flashMs ?? 1500);

  const [awaitingSpace, setAwaitingSpace] = useState(false);

  const input = useRoundInput({
    targetText,
    mode,
    roundId: round.template.id,
    roundTitle: round.template.title,
    presentationKind: 'echo',
    strictCase,
    allowAltLayer,
    keyboardLayoutId: keyboardLayoutId ?? keyboardLayout,
    onFinish,
    onAdvance: ({ cursor, char, completed }) => {
      if (char === ' ') {
        setAwaitingSpace(false);
        return;
      }
      if (completed) {
        setAwaitingSpace(false);
        return;
      }
      const needsSpace =
        cursor < targetText.length && targetText[cursor] === ' ';
      setAwaitingSpace(needsSpace);
    },
  });

  const words = useMemo(() => splitWords(targetText), [targetText]);
  const currentWordIdx = activeWordIndex(
    words,
    input.cursor,
    awaitingSpace,
  );
  const currentWord = currentWordIdx >= 0 ? words[currentWordIdx] : null;

  const flashMs = currentWord
    ? flashDurationForWord(baseFlashMs, currentWord.text.length)
    : baseFlashMs;

  // Reveal logic: each time the current word changes (or a mistake happens)
  // we re-show the word for `flashMs` (or flashMs/2 after a mistake).
  const [visible, setVisible] = useState(true);
  const lastMistakesRef = useRef(input.mistakes);
  const lastWordIdxRef = useRef(-1);

  useEffect(() => {
    if (awaitingSpace || !currentWord) return undefined;

    const wordChanged = currentWordIdx !== lastWordIdxRef.current;
    const mistakeAdded = input.mistakes > lastMistakesRef.current;
    lastWordIdxRef.current = currentWordIdx;
    lastMistakesRef.current = input.mistakes;

    if (wordChanged || mistakeAdded) {
      setVisible(true);
      const duration = mistakeAdded && !wordChanged ? flashMs / 2 : flashMs;
      const id = window.setTimeout(() => setVisible(false), duration);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [currentWordIdx, currentWord, input.mistakes, flashMs, awaitingSpace]);

  const totalWords = words.length;
  const wordNum = currentWordIdx < 0 ? totalWords : currentWordIdx + 1;
  const typedInCurrent =
    currentWord !== null
      ? Math.max(0, input.cursor - currentWord.start)
      : 0;

  return (
    <>
      <div className="grid grid-cols-4 gap-3">
        <Stat label="Tid" value={formatMs(input.elapsedMs)} />
        <Stat label="WPM" value={input.liveWpm.toFixed(0)} />
        <Stat
          label="Nøjagtighed"
          value={`${(input.liveAcc * 100).toFixed(0)}%`}
        />
        <Stat
          label="Ord"
          value={`${Math.min(wordNum, totalWords)} / ${totalWords}`}
        />
      </div>

      <div className="surface flex min-h-[14rem] flex-col items-center justify-center gap-3 p-8">
        <p className="label">
          {awaitingSpace ? 'Ord godkendt' : 'Flash-genkald'}
        </p>
        {currentWord ? (
          awaitingSpace ? (
            <>
              <p
                className="font-mono text-5xl font-semibold tracking-wide text-emerald-600 dark:text-emerald-300"
                aria-live="polite"
              >
                {currentWord.text}
              </p>
              <SpaceAdvanceHint />
            </>
          ) : (
            <>
              <EchoWordDisplay
                word={currentWord.text}
                wordStart={currentWord.start}
                cursor={input.cursor}
                correctMask={input.correctMask}
                flashed={visible}
              />
              <p className="font-mono text-sm text-faint">
                Skrevet: {typedInCurrent} / {currentWord.text.length}
              </p>
            </>
          )
        ) : (
          <p className="text-2xl font-semibold text-emerald-600 dark:text-emerald-300">
            Færdig
          </p>
        )}
        {input.lastPress && input.lastPress.correct === false && !awaitingSpace && (
          <p className="text-xs text-rose-600 dark:text-rose-300">
            Forkert tast — ordet vises kort igen
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Keyboard
          layoutId={keyboardLayout}
          nextExpectedKey={awaitingSpace ? ' ' : undefined}
          lastPressedKey={input.lastPress?.key}
          lastPressCorrect={input.lastPress?.correct}
          unlockedKeys={trin.unlockedKeys}
          hintLevel={awaitingSpace ? 'soft' : 'none'}
          showFingerDot={false}
        />
      </div>

      <p className="text-center text-xs text-faint">
        Husk ordet, og skriv det fra hukommelse. Når ordet er godkendt, tryk
        mellemrum for det næste. Forkerte tegn tæller som fejl, men du kommer
        ikke videre før du rammer den rigtige tast. Tryk Escape for at afslutte
        runden tidligt.
      </p>
    </>
  );
}

interface EchoWordDisplayProps {
  word: string;
  wordStart: number;
  cursor: number;
  correctMask: ReadonlyArray<boolean>;
  flashed: boolean;
}

function EchoWordDisplay({
  word,
  wordStart,
  cursor,
  correctMask,
  flashed,
}: EchoWordDisplayProps) {
  if (flashed) {
    return (
      <p
        className="font-mono text-5xl font-semibold tracking-wide text-strong animate-fade-in-up"
        aria-live="polite"
      >
        {word}
      </p>
    );
  }

  return (
    <p
      className="font-mono text-5xl leading-relaxed tracking-wide"
      aria-live="polite"
    >
      {[...word].map((ch, i) => {
        const absIdx = wordStart + i;
        const typed = correctMask[absIdx];
        const isCursor = absIdx === cursor;

        if (typed) {
          return (
            <span
              key={i}
              className="font-semibold text-emerald-600 dark:text-emerald-300"
            >
              {ch}
            </span>
          );
        }
        if (isCursor) {
          return (
            <span
              key={i}
              className="inline-block min-w-[0.6ch] rounded-sm border-l-2 border-indigo-500 bg-indigo-100 text-zinc-400 dark:border-indigo-400 dark:bg-indigo-500/10 dark:text-zinc-500"
            >
              ·
            </span>
          );
        }
        return (
          <span key={i} className="text-zinc-300 dark:text-zinc-600">
            ·
          </span>
        );
      })}
    </p>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="surface-soft px-4 py-3">
      <p className="label">{label}</p>
      <p className="mt-1 font-mono text-2xl text-strong">{value}</p>
    </div>
  );
}

function formatMs(ms: number): string {
  const total = Math.ceil(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}
